(() => {
  if (window.wvrReferenceExtrasLoaded) return;
  window.wvrReferenceExtrasLoaded = true;
  const cleanups = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-wvre-finder]').forEach((finder) => {
      if (finder.dataset.wvreReady) return;
      finder.dataset.wvreReady = 'true';
      const panels = [...finder.querySelectorAll('[data-wvre-step]')];
      const categories = [...finder.querySelectorAll('[data-wvre-category]')];
      let category = null;
      const productList = finder.querySelector('[data-wvre-products]');
      const filters = finder.querySelector('[data-wvre-filters]');
      const minimum = finder.querySelector('[data-wvre-price-min]');
      const maximum = finder.querySelector('[data-wvre-price-max]');
      const availableOnly = finder.querySelector('[data-wvre-available-only]');
      const count = finder.querySelector('[data-wvre-product-count]');
      const filterProducts = () => {
        if (!productList || !filters) return;
        const lower = minimum.value === '' ? 0 : Math.round(minimum.valueAsNumber * 100);
        const upper = maximum.value === '' ? Infinity : Math.round(maximum.valueAsNumber * 100);
        const invalid = !minimum.validity.valid || !maximum.validity.valid || lower > upper;
        minimum.setAttribute('aria-invalid', String(invalid));
        maximum.setAttribute('aria-invalid', String(invalid));
        finder.querySelector('[data-wvre-filter-error]').hidden = !invalid;
        const products = [...productList.querySelectorAll('[data-wvre-product]')];
        products.forEach((product) => {
          const price = Number(product.dataset.wvrePrice);
          product.hidden = invalid || price < lower || price > upper || (availableOnly.checked && product.dataset.wvreAvailable !== 'true');
        });
        const shown = products.filter((product) => !product.hidden).length;
        count.textContent = invalid ? '' : count.dataset.wvreCountTemplate.replace('{shown}', shown).replace('{total}', products.length);
        finder.querySelector('[data-wvre-empty]').hidden = invalid || !products.length || shown > 0;
        finder.querySelector('[data-wvre-no-products]').hidden = products.length > 0;
        filters.hidden = products.length === 0;
      };
      const resetFilters = () => {
        filters?.reset();
        filterProducts();
      };
      filters?.addEventListener('input', filterProducts);
      filters?.addEventListener('submit', (event) => event.preventDefault());
      const updateCategory = () => {
        finder.querySelector('[data-wvre-summary-category]').textContent = category?.label || '';
        categories.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.wvreCategory === category?.key));
        });
      };
      const show = (step) => {
        finder.dataset.wvreView = step;
        panels.forEach((panel) => { panel.hidden = panel.dataset.wvreStep !== step; });
        finder.querySelector(`[data-wvre-step="${step}"] h3`).focus({ preventScroll: true });
      };
      finder.querySelector('[data-wvre-finder-fallback]').hidden = true;
      finder.querySelector('[data-wvre-finder-flow]').hidden = false;
      finder.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !finder.contains(button)) return;
        if (button.hasAttribute('data-wvre-clear-filters')) {
          resetFilters();
          return;
        }
        if (button.hasAttribute('data-wvre-result-url')) {
          category = { key: button.dataset.wvreCategory, label: button.querySelector('[data-wvre-choice-label]').textContent.trim() };
          updateCategory();
          finder.querySelector('[data-wvre-result-link]').setAttribute('href', button.dataset.wvreResultUrl);
          finder.querySelector('[data-wvre-result-text]').textContent = button.dataset.wvreResultLabel;
          if (productList) {
            const template = finder.querySelector(`[data-wvre-product-template="${button.dataset.wvreCategory}"]`);
            productList.replaceChildren(...(template ? [template.content.cloneNode(true)] : []));
            resetFilters();
          }
          show('result');
        }
        if (button.hasAttribute('data-wvre-restart')) {
          category = null;
          productList?.replaceChildren();
          resetFilters();
          updateCategory();
          show('category');
        }
      });
    });
    scope.querySelectorAll('[data-wvre-gallery]').forEach((gallery) => {
      if (gallery.dataset.wvreReady) return;
      const cards = [...gallery.querySelectorAll('[data-wvre-scene]')];
      const viewport = gallery.querySelector('[data-wvre-viewport]');
      const controls = gallery.querySelector('[data-wvre-controls]');
      const previous = gallery.querySelector('[data-wvre-previous]');
      const next = gallery.querySelector('[data-wvre-next]');
      const status = gallery.querySelector('[data-wvre-status]');
      if (!viewport || !controls || !previous || !next || !cards.length) return;
      gallery.dataset.wvreReady = 'true';
      const controller = new AbortController();
      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: controller.signal });
      let requestedPosition = null;
      let frame = 0;
      let settleTimer;
      const playbackDuration = 4000;
      let playbackFrame = 0;
      let remainingTime = playbackDuration;
      let playbackDeadline = 0;
      let transitioning = false;
      let visible = false;
      let hovered = false;
      let touching = false;
      let userPaused = false;
      let selectedScene = 0;
      let automaticMove = false;
      let navigationMove = false;
      const playback = gallery.querySelector('[data-wvre-playback]');
      const progressRing = playback?.querySelector('[data-wvre-ring-progress]');
      const navigation = gallery.querySelector('[data-wvre-navigation]');
      const dialog = gallery.querySelector('[data-wvre-dialog]');
      const sceneButtons = cards.map((card, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = card.querySelector('h3').textContent;
        button.setAttribute('aria-controls', viewport.id);
        listen(button, 'click', event => {
          selectedScene = index;
          moveTo(index * step(), event.detail === 0, false, index);
        });
        return button;
      });
      if (navigation) {
        navigation.replaceChildren(...sceneButtons);
        navigation.hidden = cards.length < 2;
      }
      const updateSelection = () => {
        sceneButtons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === selectedScene)));
        cards.forEach((card, index) => card.toggleAttribute('data-wvre-selected', index === selectedScene));
        if (navigation) {
          const selectedBounds = sceneButtons[selectedScene].getBoundingClientRect();
          const navigationBounds = navigation.getBoundingClientRect();
          if (selectedBounds.left < navigationBounds.left || selectedBounds.right > navigationBounds.right) {
            navigation.scrollTo({ left: navigation.scrollLeft + selectedBounds.left - navigationBounds.left, behavior: 'instant' });
          }
        }
      };
      const renderProgress = () => {
        if (progressRing) progressRing.style.strokeDashoffset = String(100 * remainingTime / playbackDuration);
      };
      const stopPlayback = (reset = false) => {
        if (playbackDeadline) remainingTime = Math.max(0, playbackDeadline - performance.now());
        playbackDeadline = 0;
        cancelAnimationFrame(playbackFrame);
        playbackFrame = 0;
        if (reset) remainingTime = playbackDuration;
        renderProgress();
      };
      const advancePlayback = () => {
        remainingTime = Math.max(0, playbackDeadline - performance.now());
        renderProgress();
        if (remainingTime <= 0) {
          playbackDeadline = 0;
          playbackFrame = 0;
          const atEnd = viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 2;
          moveTo(atEnd ? 0 : viewport.scrollLeft + step(), atEnd, true);
        } else {
          playbackFrame = requestAnimationFrame(advancePlayback);
        }
      };
      const syncPlayback = () => {
        if (!playback) return;
        const unavailable = reducedMotion.matches || Boolean(window.Shopify?.designMode);
        const paused = userPaused || unavailable;
        playback.disabled = unavailable;
        playback.setAttribute('aria-label', paused ? playback.dataset.playLabel : playback.dataset.pauseLabel);
        playback.querySelector('[data-wvre-pause-icon]').hidden = paused;
        playback.querySelector('[data-wvre-play-icon]').hidden = !paused;
        const focused = gallery.contains(document.activeElement) && document.activeElement !== playback;
        if (paused || !visible || hovered || touching || focused || transitioning || dialog?.open || document.hidden || viewport.scrollWidth <= viewport.clientWidth + 2) {
          stopPlayback();
          return;
        }
        if (playbackDeadline) return;
        playbackDeadline = performance.now() + remainingTime;
        playbackFrame = requestAnimationFrame(advancePlayback);
      };
      const step = () => cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
      const updateControls = () => {
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const position = requestedPosition ?? viewport.scrollLeft;
        controls.hidden = maximum < 2;
        previous.disabled = position < 2;
        next.disabled = position >= maximum - 2;
      };
      const announce = () => {
        if (!status) return;
        const distance = step();
        const first = Math.min(cards.length - 1, Math.round(viewport.scrollLeft / distance));
        const gap = parseFloat(getComputedStyle(viewport).columnGap) || 0;
        const shown = Math.max(1, Math.floor((viewport.clientWidth + gap + 1) / distance));
        const message = status.dataset.template.replace('{first}', first + 1).replace('{last}', Math.min(cards.length, first + shown)).replace('{total}', cards.length);
        if (status.textContent !== message) status.textContent = message;
      };
      const moveTo = (position, instant = false, automatic = false, selectedIndex = null) => {
        stopPlayback(true);
        automaticMove = automatic;
        navigationMove = selectedIndex !== null;
        if (status) status.setAttribute('aria-live', automatic ? 'off' : 'polite');
        requestedPosition = Math.max(0, Math.min(position, viewport.scrollWidth - viewport.clientWidth));
        transitioning = Math.abs(requestedPosition - viewport.scrollLeft) > 1;
        if (!navigationMove) selectedScene = Math.min(cards.length - 1, Math.round(requestedPosition / step()));
        updateSelection();
        viewport.scrollTo({ left: requestedPosition, behavior: instant || reducedMotion.matches ? 'instant' : 'smooth' });
        updateControls();
        syncPlayback();
      };
      const move = (direction, instant = false) => moveTo((requestedPosition ?? viewport.scrollLeft) + direction * step(), instant);
      listen(previous, 'click', event => move(-1, event.detail === 0));
      listen(next, 'click', event => move(1, event.detail === 0));
      listen(viewport, 'keydown', event => {
        if (event.target !== viewport || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') moveTo(0, true);
        else if (event.key === 'End') moveTo(viewport.scrollWidth, true);
        else move(event.key === 'ArrowLeft' ? -1 : 1, true);
      });
      const releasePosition = () => { requestedPosition = null; navigationMove = false; automaticMove = false; if (status) status.setAttribute('aria-live', 'polite'); syncPlayback(); };
      listen(viewport, 'pointerdown', releasePosition, { passive: true });
      listen(viewport, 'wheel', releasePosition, { passive: true });
      listen(viewport, 'scroll', () => {
        if (!frame) frame = requestAnimationFrame(() => { frame = 0; updateControls(); });
        clearTimeout(settleTimer);
        transitioning = true;
        stopPlayback(true);
        settleTimer = setTimeout(() => {
          requestedPosition = null;
          if (!navigationMove) selectedScene = Math.min(cards.length - 1, Math.round(viewport.scrollLeft / step()));
          navigationMove = false;
          transitioning = false;
          updateControls();
          updateSelection();
          if (!automaticMove) announce();
          syncPlayback();
        }, 150);
      }, { passive: true });
      const resize = () => { requestedPosition = null; updateControls(); announce(); syncPlayback(); };
      const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
      if (observer) observer.observe(viewport);
      else listen(window, 'resize', resize);
      if (playback) listen(playback, 'click', () => { userPaused = !userPaused; syncPlayback(); });
      listen(gallery, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; syncPlayback(); } });
      listen(gallery, 'pointerleave', () => { hovered = false; syncPlayback(); });
      listen(gallery, 'pointerdown', event => { if (event.pointerType !== 'mouse') { touching = true; syncPlayback(); } }, { passive: true });
      const releaseTouch = () => { touching = false; syncPlayback(); };
      listen(window, 'pointerup', releaseTouch, { passive: true });
      listen(window, 'pointercancel', releaseTouch, { passive: true });
      listen(gallery, 'focusin', syncPlayback);
      listen(gallery, 'focusout', () => queueMicrotask(() => { if (!controller.signal.aborted) syncPlayback(); }));
      listen(document, 'visibilitychange', syncPlayback);
      listen(reducedMotion, 'change', syncPlayback);
      const visibilityObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .35;
        syncPlayback();
      }, { threshold: [0, .35] }) : null;
      visibilityObserver?.observe(viewport);
      if (dialog && typeof dialog.showModal === 'function') {
        const images = [...gallery.querySelectorAll('[data-wvre-enlarge]')];
        const dialogImage = dialog.querySelector('[data-wvre-dialog-image]');
        const dialogCount = dialog.querySelector('[data-wvre-dialog-count]');
        let activeIndex = 0;
        let opener = null;
        const displayImage = (index) => {
          activeIndex = (index + images.length) % images.length;
          const link = images[activeIndex];
          dialog.querySelector('[data-wvre-dialog-title]').textContent = link.dataset.wvreTitle || '';
          dialogImage.src = link.href;
          dialogImage.alt = link.dataset.wvreCaption || '';
          dialog.querySelector('[data-wvre-dialog-caption]').textContent = link.dataset.wvreCaption || '';
          dialogCount.textContent = dialogCount.dataset.wvreCountTemplate.replace('{shown}', activeIndex + 1).replace('{total}', images.length);
        };
        images.forEach((link, index) => {
          link.setAttribute('aria-haspopup', 'dialog');
          listen(link, 'click', (event) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            opener = link;
            displayImage(index);
            dialog.showModal();
            syncPlayback();
          });
        });
        listen(dialog.querySelector('[data-wvre-dialog-close]'), 'click', () => dialog.close());
        dialog.querySelector('[data-wvre-dialog-previous]').disabled = images.length < 2;
        dialog.querySelector('[data-wvre-dialog-next]').disabled = images.length < 2;
        listen(dialog.querySelector('[data-wvre-dialog-previous]'), 'click', () => displayImage(activeIndex - 1));
        listen(dialog.querySelector('[data-wvre-dialog-next]'), 'click', () => displayImage(activeIndex + 1));
        listen(dialog, 'keydown', (event) => {
          if (event.key === 'Tab') {
            const controls = [...dialog.querySelectorAll('button:not(:disabled)')];
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          displayImage(activeIndex + (event.key === 'ArrowLeft' ? -1 : 1));
        });
        listen(dialog, 'close', () => {
          if (opener?.isConnected) opener.focus({ preventScroll: true });
          dialogImage.removeAttribute('src');
          syncPlayback();
        });
      }
      updateControls();
      announce();
      updateSelection();
      syncPlayback();
      cleanups.set(gallery, () => {
        if (dialog?.open) dialog.close();
        controller.abort();
        observer?.disconnect();
        visibilityObserver?.disconnect();
        stopPlayback();
        clearTimeout(settleTimer);
        cancelAnimationFrame(frame);
        delete gallery.dataset.wvreReady;
      });
    });
  };
  const revealFinder = () => {
    if (window.location.hash !== '#wv-home-finder') return;
    const finder = document.getElementById('wv-home-finder');
    const disclosure = finder?.closest('.wvr-finder-disclosure');
    if (disclosure && !disclosure.open) {
      disclosure.open = true;
      finder.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  };
  const start = () => { initialize(); revealFinder(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('hashchange', revealFinder);
  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[href]');
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const comparison = document.querySelector('[data-wvr-comparison][data-ready="true"]');
    if (comparison && typeof comparison.showModal === 'function' && anchor.matches('[data-wvr-compare-open], [data-wvr-compare-product]')) return;
    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== location.origin || destination.pathname !== location.pathname || destination.hash !== '#wv-home-finder') return;
    const disclosure = document.getElementById('wv-home-finder')?.closest('.wvr-finder-disclosure');
    if (disclosure) disclosure.open = true;
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-wvre-gallery]').forEach((gallery) => cleanups.get(gallery)?.());
  });
})();
